import NA60Validator from 'shared/ncts/EE/NA60'
import NA61Validator from 'shared/ncts/EE/NA61'
import NA62Validator from 'shared/ncts/EE/NA62'
import { hasText } from 'shared/lib/common-util'
import { createFieldRequirementGetter, matchServerRequirement } from '../../../resolverFactory'
import { FieldRequirementResolverContext } from '../types'
import {
  FieldRequirement,
  FieldRequirementEnvironment,
  FieldRequirementResolverMap,
  forbidden,
  optional,
  required,
  toFieldRequirement,
} from '../../../../../../models/fieldRules'
import C0933Validator from '../../../../../../../../shared/ncts/p6/conditions/C0933'
import C0943Validator from '../../../../../../../../shared/ncts/p6/conditions/C0943'
import C0003Validator from '../../../../../../../../shared/ncts/p6/conditions/C0003'
import C0001Validator from '../../../../../../../../shared/ncts/p6/conditions/C0001'
import {
  isBuyerPresent, isConsigneePresent, isConsignorPresent, isSellerPresent,
} from '../../../../../../../../shared/ncts/common/types'
import C0934Validator from '../../../../../../../../shared/ncts/p6/conditions/C0934'
import resolveEffectiveRequirement from '../../../resolveEffectiveRequirement'
import C0252Validator from '../../../../../../../../shared/ncts/p6/conditions/C0252'
import C0250Validator from '../../../../../../../../shared/ncts/p6/conditions/C0250'
import C0506Validator from '../../../../../../../../shared/ncts/p6/conditions/C0506'
import C0505Validator from '../../../../../../../../shared/ncts/p6/conditions/C0505'
import C0931Validator from '../../../../../../../../shared/ncts/p6/conditions/C0931'
import CustomValidators from '../../../../../../../../shared/ncts/validation'
import C0542Validator from '../../../../../../../../shared/ncts/p6/conditions/C0542'
import C0544Validator from '../../../../../../../../shared/ncts/p6/conditions/C0544'
import C0942Validator from '../../../../../../../../shared/ncts/p6/conditions/C0942'
import C0036Validator from '../../../../../../../../shared/ncts/p6/conditions/C0036'
import C0037Validator from '../../../../../../../../shared/ncts/p6/conditions/C0037'

function ruleForPreviousDocumentDocumentType(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (ctx.country !== 'EE') return 'required'

  return toFieldRequirement(NA60Validator.validatePreviousDocumentDocumentTypeRequirement(ctx.previousDocument))
}

function ruleForPreviousDocumentProcedureType(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (ctx.country !== 'EE') return 'forbidden'

  const documentTypeRequirement = ruleForPreviousDocumentDocumentType(env)
  if (documentTypeRequirement === 'forbidden') return 'optional'

  return toFieldRequirement(NA60Validator.validatePreviousDocumentDocumentTypeRequirement(ctx.previousDocument))
}

function ruleForPreviousDocumentReferenceNumber(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (ctx.country !== 'EE') return 'required'

  let requirement = toFieldRequirement(NA61Validator.validatePreviousDocumentRefNumberRequirement(ctx.previousDocument))
  if (requirement === 'optional') {
    requirement = toFieldRequirement(NA62Validator.validatePreviousDocumentReferenceNumberRequirement(ctx.previousDocument))

    if (requirement === 'forbidden' && hasText(ctx.previousDocument?.referenceNumber)) {
      requirement = 'optional'
    }
  }

  return requirement
}

function ruleForPlaceOfAcceptanceActual(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  return toFieldRequirement(C0933Validator.validateHouseConsignmentPlaceOfAcceptanceRequirement(ctx))
}

function ruleForPlaceOfAcceptance(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const requirement = ruleForPlaceOfAcceptanceActual(env)

  return ctx.differentPlaceOfAcceptance !== false ? requirement : 'forbidden'
}

function ruleForPlaceOfAcceptanceCountry(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const requirement = ruleForPlaceOfAcceptanceActual(env)

  return ctx.differentPlaceOfAcceptance !== false ? requirement : 'forbidden'
}

function ruleForPlaceOfAcceptanceLocation(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const requirement = ruleForPlaceOfAcceptanceActual(env)

  return ctx.differentPlaceOfAcceptance !== false ? requirement : 'forbidden'
}

function ruleForPlaceOfDeliveryActual(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  return toFieldRequirement(C0933Validator.validateHouseConsignmentPlaceOfAcceptanceRequirement(ctx))
}

function ruleForPlaceOfDelivery(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const requirement = ruleForPlaceOfDeliveryActual(env)

  return ctx.differentPlaceOfDelivery !== false ? requirement : 'forbidden'
}

function ruleForPlaceOfDeliveryCountry(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const requirement = ruleForPlaceOfDeliveryActual(env)

  return ctx.differentPlaceOfAcceptance !== false ? requirement : 'forbidden'
}

function ruleForPlaceOfDeliveryLocation(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const requirement = ruleForPlaceOfDeliveryActual(env)

  return ctx.differentPlaceOfAcceptance !== false ? requirement : 'forbidden'
}

function ruleForCountriesOfRoutingActual(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  return toFieldRequirement(C0943Validator.validateCountryOfRoutingOfConsignmentRequirement(ctx))
}

function ruleForCountriesOfRouting(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const requirement = ruleForCountriesOfRoutingActual(env)

  return ctx.differentCountriesOfRouting !== false ? requirement : 'forbidden'
}

function ruleForConsignee(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const ruleConditions = ctx.serverValidationResult?.ruleConditions

  const requirement = toFieldRequirement(ctx.isOptIn
    ? C0003Validator.validateHouseConsignmentConsigneeRequirement({
      security: ctx.security,
      countryOfDestination: ruleConditions?.consignmentCountryOfDestination ?? '',
      specificCircumstanceIndicator: ctx.specificCircumstanceIndicator ?? '',
      houseCountryOfDestination: ctx.houseCountryOfDestination,
      ...ruleConditions,
    })
    : C0001Validator.validateConsigneeRequirementForHouseConsignment({
      security: ctx.security,
      countryOfDestination: ruleConditions?.consignmentCountryOfDestination ?? '',
      houseCountryOfDestination: ctx.houseCountryOfDestination,
      ...ruleConditions,
    }))

  return requirement === 'optional' && isConsigneePresent(ctx.consignee) ? 'required' : requirement
}

function ruleForConsigneeNameAndAddress(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const consigneeRequirement = env.getFieldRequirement('consignee') ?? 'optional'
  const requirement = toFieldRequirement(ctx.isOptIn
    ? C0252Validator.validateNameAndAddressRequirement({
      security: ctx.security,
      identificationNumber: ctx.consignee?.identificationNumber ?? '',
    })
    : C0250Validator.validateNameAndAddressRequirement(ctx.consignee?.identificationNumber))

  return resolveEffectiveRequirement(consigneeRequirement, requirement)
}

function ruleForConsigneeAddressAddressLine(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const consigneeAddressRequirement = env.getFieldRequirement('consignee.address') ?? 'optional'
  const requirement = ctx.isOptIn
    ? toFieldRequirement(C0036Validator.validateHouseConsignmentConsigneeAddressLineRequirement(ctx))
    : 'required'

  return resolveEffectiveRequirement(consigneeAddressRequirement, requirement)
}

function ruleForConsigneeAddressStreet(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const consigneeAddressRequirement = env.getFieldRequirement('consignee.address') ?? 'optional'
  const requirement = ctx.isOptIn
    ? toFieldRequirement(C0037Validator.validateHouseConsignmentConsigneeStreetRequirement(ctx))
    : 'forbidden'

  return resolveEffectiveRequirement(consigneeAddressRequirement, requirement)
}

function ruleForConsigneeAddressPostcode(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const consigneeRequirement = env.getFieldRequirement('consignee.address') ?? 'optional'
  const requirement = toFieldRequirement(ctx.isOptIn
    ? C0506Validator.validateStandardPostcodeRequirement(ctx.consignee?.address?.country ?? '')
    : C0505Validator.validatePostcodeRequirement(ctx.consignee?.address?.country ?? ''))

  return resolveEffectiveRequirement(consigneeRequirement, requirement)
}

function ruleForConsigneeAddressStreetNumber(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (!ctx.isOptIn) return 'forbidden'

  return env.getFieldRequirement('consignee.address.street') ?? 'optional'
}

function ruleForConsigneeContactPerson(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (!ctx.isOptIn) return 'forbidden'

  const consigneeRequirement = env.getFieldRequirement('consignee') ?? 'optional'
  const requirement = toFieldRequirement(C0931Validator.validateHouseConsignmentContactPersonRequirement(ctx))

  return resolveEffectiveRequirement(consigneeRequirement, requirement)
}

function ruleForConsigneeContactPersonNameAndPhone(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const contactPersonRequirement = env.getFieldRequirement('consignee.contactPerson') ?? 'optional'
  const requirement = CustomValidators.validateContactPersonNameAndPhoneRequirement(ctx.consignee?.contactPerson) ? 'required' : 'optional'

  return resolveEffectiveRequirement(
    contactPersonRequirement,
    requirement,
  )
}

function ruleForConsigneeTypeOfPerson(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env
  if (!ctx.isOptIn) return 'forbidden'

  const consigneeRequirement = env.getFieldRequirement('consignee') ?? 'optional'
  const requirement = toFieldRequirement(C0934Validator.validateHouseConsignmentConsigneeTypeOfPersonRequirement(ctx))

  return resolveEffectiveRequirement(consigneeRequirement, requirement)
}

function ruleForConsignorAddressAddressLine(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const consignorAddressRequirement = env.getFieldRequirement('consignor.address') ?? 'optional'
  const requirement = ctx.isOptIn
    ? toFieldRequirement(C0036Validator.validateHouseConsignmentConsignorAddressLineRequirement(ctx))
    : 'required'

  return resolveEffectiveRequirement(consignorAddressRequirement, requirement)
}

function ruleForConsignorAddressStreet(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const consignorAddressRequirement = env.getFieldRequirement('consignor.address') ?? 'optional'
  const requirement = ctx.isOptIn
    ? toFieldRequirement(C0037Validator.validateHouseConsignmentConsignorStreetRequirement(ctx))
    : 'forbidden'

  return resolveEffectiveRequirement(consignorAddressRequirement, requirement)
}

function ruleForConsignorAddressStreetNumber(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  if (!ctx.isOptIn) return 'forbidden'

  return env.getFieldRequirement('consignor.address.street') ?? 'optional'
}

function ruleForConsignorAddressPostcode(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const addressRequirement = env.getFieldRequirement('consignor.address') ?? 'optional'
  const requirement = toFieldRequirement(ctx.isOptIn
    ? C0506Validator.validateStandardPostcodeRequirement(ctx.consignor?.address?.country ?? '')
    : C0505Validator.validatePostcodeRequirement(ctx.consignor?.address?.country ?? ''))

  return resolveEffectiveRequirement(addressRequirement, requirement)
}

function ruleForConsignorContactPerson(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const consignorRequirement = env.getFieldRequirement('consignor') ?? 'optional'
  const requirement = ctx.isOptIn
    ? toFieldRequirement(C0931Validator.validateHouseConsignmentContactPersonRequirement(ctx))
    : 'optional'

  return resolveEffectiveRequirement(consignorRequirement, requirement)
}

function ruleForConsignorContactPersonNameAndPhone(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const contactPersonRequirement = env.getFieldRequirement('consignor.contactPerson') ?? 'optional'
  const requirement = CustomValidators.validateContactPersonNameAndPhoneRequirement(ctx.consignor?.contactPerson) ? 'required' : 'optional'

  return resolveEffectiveRequirement(
    contactPersonRequirement,
    requirement,
  )
}

function ruleForConsignor(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const ruleConditions = ctx.serverValidationResult?.ruleConditions

  const requirement = toFieldRequirement(ctx.isOptIn
    ? C0544Validator.validateHouseConsignmentConsignorRequirement({
      security: ctx.security,
      specificCircumstanceIndicator: ctx.specificCircumstanceIndicator ?? '',
      reducedDatasetIndicator: '',
      isConsignmentConsignorPresent: ruleConditions?.isConsignmentConsignorPresent ?? false,
    })
    : C0542Validator.validateHouseConsignmentConsignorRequirement({
      security: ctx.security,
      reducedDatasetIndicator: '',
      isConsignmentConsignorPresent: ruleConditions?.isConsignmentConsignorPresent ?? false,
    }))

  return requirement === 'optional' && isConsignorPresent(ctx.consignor) ? 'required' : requirement
}

function ruleForConsignorTypeOfPerson(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env
  if (!ctx.isOptIn) return 'forbidden'

  const consignorRequirement = env.getFieldRequirement('consignor') ?? 'optional'
  const requirement = toFieldRequirement(C0934Validator.validateHouseConsignmentConsignorTypeOfPersonRequirement(ctx))

  return resolveEffectiveRequirement(consignorRequirement, requirement)
}

function ruleForConsignorNameAndAddress(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const consignorRequirement = env.getFieldRequirement('consignor') ?? 'optional'

  const requirement = toFieldRequirement(ctx.isOptIn
    ? C0252Validator.validateNameAndAddressRequirement({
      security: ctx.security,
      identificationNumber: ctx.consignor?.identificationNumber ?? '',
    })
    : C0250Validator.validateNameAndAddressRequirement(ctx.consignor?.identificationNumber))

  return resolveEffectiveRequirement(consignorRequirement, requirement)
}

function ruleForBuyer(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env
  if (!ctx.isOptIn) return 'forbidden'
  if (!ctx.differentBuyer) return 'forbidden'

  const ruleConditions = ctx.serverValidationResult?.ruleConditions
  const consignmentPlaceOfDeliveryCountry = ruleConditions?.consignmentPlaceOfDeliveryCountry ?? ''

  const placeOfDeliveryCountry = ctx.differentPlaceOfDelivery !== false
    ? ctx.placeOfDelivery?.country : consignmentPlaceOfDeliveryCountry

  const requirement = toFieldRequirement(C0942Validator.validateBuyerRequirement({
    security: ctx.security,
    placeOfDeliveryCountry: placeOfDeliveryCountry ?? '',
    unLocode: '',
    additionalDeclarationType: ctx.additionalDeclarationType,
  }))

  return requirement === 'optional' && isBuyerPresent(ctx.buyer) ? 'required' : requirement
}

function ruleForBuyerAddressPostcode(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const addressRequirement = env.getFieldRequirement('buyer.address') ?? 'optional'
  const requirement = toFieldRequirement(C0506Validator.validateSecurityPostcodeRequirement({
    security: ctx.security,
    country: ctx.buyer?.address?.country ?? '',
  }))

  return resolveEffectiveRequirement(addressRequirement, requirement)
}

function ruleForBuyerAddressStreet(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (!ctx.isOptIn) return 'forbidden'

  return env.getFieldRequirement('buyer.address') ?? 'optional'
}

function ruleForBuyerAddressStreetNumber(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  return env.getFieldRequirement('buyer.address.street') ?? 'optional'
}

function ruleForSeller(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env
  if (!ctx.isOptIn) return 'forbidden'
  if (!ctx.differentSeller) return 'forbidden'

  const ruleConditions = ctx.serverValidationResult?.ruleConditions
  const consignmentPlaceOfDeliveryCountry = ruleConditions?.consignmentPlaceOfDeliveryCountry ?? ''

  const placeOfDeliveryCountry = ctx.differentPlaceOfDelivery !== false
    ? ctx.placeOfDelivery?.country : consignmentPlaceOfDeliveryCountry

  const requirement = toFieldRequirement(C0942Validator.validateSellerRequirement({
    security: ctx.security,
    placeOfDeliveryCountry: placeOfDeliveryCountry ?? '',
    unLocode: '',
    additionalDeclarationType: ctx.additionalDeclarationType,
  }))

  return requirement === 'optional' && isSellerPresent(ctx.seller) ? 'required' : requirement
}

function ruleForSellerAddressPostcode(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const addressRequirement = env.getFieldRequirement('seller.address') ?? 'optional'
  const requirement = toFieldRequirement(C0506Validator.validateSecurityPostcodeRequirement({
    security: ctx.security,
    country: ctx.seller?.address?.country ?? '',
  }))

  return resolveEffectiveRequirement(addressRequirement, requirement)
}

function ruleForSellerAddressStreet(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (!ctx.isOptIn) return 'forbidden'

  return env.getFieldRequirement('seller.address') ?? 'optional'
}

function ruleForSellerAddressStreetNumber(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  return env.getFieldRequirement('seller.address.street') ?? 'optional'
}

const fieldRequirementResolvers: FieldRequirementResolverMap<FieldRequirementResolverContext> = {
  additionalInformation: forbidden,
  additionalReference: forbidden,
  additionalSupplyChainActor: forbidden,
  buyer: ruleForBuyer,
  'buyer.address.postcode': ruleForBuyerAddressPostcode,
  'buyer.address.street': ruleForBuyerAddressStreet,
  'buyer.address.streetNumber': ruleForBuyerAddressStreetNumber,
  'buyer.contactPerson.email': optional,
  'buyer.identificationNumber': optional,
  consignee: ruleForConsignee,
  'consignee.address': ruleForConsigneeNameAndAddress,
  'consignee.address.addressLine': ruleForConsigneeAddressAddressLine,
  'consignee.address.postcode': ruleForConsigneeAddressPostcode,
  'consignee.address.street': ruleForConsigneeAddressStreet,
  'consignee.address.streetNumber': ruleForConsigneeAddressStreetNumber,
  'consignee.contactPerson': ruleForConsigneeContactPerson,
  'consignee.contactPerson.name': ruleForConsigneeContactPersonNameAndPhone,
  'consignee.contactPerson.telephone': ruleForConsigneeContactPersonNameAndPhone,
  'consignee.contactPerson.email': optional,
  'consignee.id': optional,
  'consignee.identificationNumber': optional,
  'consignee.name': ruleForConsigneeNameAndAddress,
  'consignee.typeOfPerson': ruleForConsigneeTypeOfPerson,
  consignor: ruleForConsignor,
  'consignor.address': ruleForConsignorNameAndAddress,
  'consignor.address.postcode': ruleForConsignorAddressPostcode,
  'consignor.address.addressLine': ruleForConsignorAddressAddressLine,
  'consignor.address.street': ruleForConsignorAddressStreet,
  'consignor.address.streetNumber': ruleForConsignorAddressStreetNumber,
  'consignor.contactPerson': ruleForConsignorContactPerson,
  'consignor.contactPerson.name': ruleForConsignorContactPersonNameAndPhone,
  'consignor.contactPerson.telephone': ruleForConsignorContactPersonNameAndPhone,
  'consignor.contactPerson.email': optional,
  'consignor.id': optional,
  'consignor.identificationNumber': optional,
  'consignor.name': ruleForConsignorNameAndAddress,
  'consignor.typeOfPerson': ruleForConsignorTypeOfPerson,
  containerIndicator: forbidden,
  countriesOfRouting: ruleForCountriesOfRouting,
  countryOfDestination: optional, // server-side
  countryOfDispatch: optional, // server-side
  departureTransportMeans: forbidden, // server-side
  differentCountriesOfRouting: optional,
  differentBuyer: optional,
  differentPlaceOfAcceptance: optional,
  differentPlaceOfDelivery: optional,
  differentSeller: optional,
  grossMass: required,
  passiveBorderTransportMeans: forbidden,
  placeOfAcceptance: ruleForPlaceOfAcceptance,
  'placeOfAcceptance.country': ruleForPlaceOfAcceptanceCountry,
  'placeOfAcceptance.location': ruleForPlaceOfAcceptanceLocation,
  placeOfDelivery: ruleForPlaceOfDelivery,
  'placeOfDelivery.country': ruleForPlaceOfDeliveryCountry,
  'placeOfDelivery.location': ruleForPlaceOfDeliveryLocation,
  'previousDocument.*.documentType': ruleForPreviousDocumentDocumentType,
  'previousDocument.*.procedureType': ruleForPreviousDocumentProcedureType,
  'previousDocument.*.referenceNumber': ruleForPreviousDocumentReferenceNumber,
  referenceNumberUCR: optional,
  seller: ruleForSeller,
  'seller.address.postcode': ruleForSellerAddressPostcode,
  'seller.address.street': ruleForSellerAddressStreet,
  'seller.address.streetNumber': ruleForSellerAddressStreetNumber,
  'seller.contactPerson.email': optional,
  'seller.identificationNumber': optional,
  'supportingDocument.*.documentType': required,
  'supportingDocument.*.referenceNumber': required,
  'transportDocument.*.documentType': required,
  'transportDocument.*.referenceNumber': required,
  transportEquipment: forbidden,
  transportMeans: optional,
}

function getServerRequirementForPath(
  path: string,
  ctx: FieldRequirementResolverContext,
) {
  return matchServerRequirement(ctx.serverValidationResult?.fieldRequirements, path)
}

const getFieldRequirementP6 = createFieldRequirementGetter<FieldRequirementResolverContext>(
  fieldRequirementResolvers,
  getServerRequirementForPath,
)

export default getFieldRequirementP6

